"use client";

import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Lightbulb, Shield, Heart, Zap, Users, Target, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AboutUs = () => {
    const { t } = useTranslation('about');
    const navigate = useNavigate();

    const values = [
        {
            id: 1,
            title: t('values.innovation.title'),
            description: t('values.innovation.description'),
            icon: Lightbulb
        },
        {
            id: 2,
            title: t('values.transparency.title'),
            description: t('values.transparency.description'),
            icon: Shield
        },
        {
            id: 3,
            title: t('values.commitment.title'),
            description: t('values.commitment.description'),
            icon: Heart
        }
    ];

    const services = [
        {
            id: 1,
            title: t('services.simplified.title'),
            description: t('services.simplified.description'),
            icon: Zap
        },
        {
            id: 2,
            title: t('services.personalized.title'),
            description: t('services.personalized.description'),
            icon: Users
        },
        {
            id: 3,
            title: t('services.modern.title'),
            description: t('services.modern.description'),
            icon: Target
        }
    ];

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.2,
            },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.8, ease: "easeOut" },
        },
    };

    return (
        <div className="min-h-screen bg-white dark:bg-gray-950 transition-colors">
            {/* Animated Background */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none">
                <motion.div
                    animate={{ y: [0, -30, 0], opacity: [0.1, 0.2, 0.1] }}
                    transition={{ duration: 8, repeat: Infinity }}
                    className="absolute -top-40 -right-40 w-80 h-80 bg-yellow-500/20 rounded-full blur-3xl"
                />
                <motion.div
                    animate={{ y: [0, 30, 0], opacity: [0.08, 0.15, 0.08] }}
                    transition={{ duration: 10, repeat: Infinity }}
                    className="absolute -bottom-40 -left-40 w-80 h-80 bg-yellow-400/15 rounded-full blur-3xl"
                />
            </div>

            <div className="relative z-10 container mx-auto px-4 py-8 space-y-8 md:space-y-12">
                {/* Hero Section */}
                <motion.div
                    initial={{ opacity: 0, y: -30 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 dark:from-yellow-600 dark:via-yellow-700 dark:to-yellow-800 p-8 md:p-12 lg:p-20 shadow-2xl"
                >
                    <div className="absolute inset-0 opacity-10">
                        <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                            className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent"
                        />
                    </div>

                    <div className="relative z-10">
                        <motion.h1
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.1 }}
                            className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight"
                        >
                            {t('hero.title')}
                        </motion.h1>
                        <motion.p
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.2 }}
                            className="text-yellow-50 text-base md:text-lg lg:text-xl max-w-2xl"
                        >
                            {t('hero.subtitle')}
                        </motion.p>
                    </div>
                </motion.div>

                {/* About Section */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="rounded-2xl bg-white dark:bg-gray-800 shadow-lg p-6 md:p-8 lg:p-12"
                >
                    <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-6 md:mb-8 flex items-center gap-3">
                        <div className="w-1 h-8 md:h-10 bg-gradient-to-b from-yellow-400 to-yellow-600 rounded-full" />
                        {t('sections.who')}
                    </h2>

                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        className="space-y-6"
                    >
                        <motion.p variants={itemVariants} className="text-gray-700 dark:text-gray-300 text-base md:text-lg leading-relaxed">
                            <strong className="text-yellow-600 dark:text-yellow-400">{t('who.intro.platform')}</strong> {t('who.intro.description')}
                        </motion.p>

                        <motion.p variants={itemVariants} className="text-gray-700 dark:text-gray-300 text-base md:text-lg leading-relaxed">
                            {t('who.vision')}
                        </motion.p>

                        <motion.div variants={itemVariants} className="bg-yellow-50 dark:bg-yellow-900/10 border-l-4 border-yellow-500 p-4 md:p-6 rounded-r-lg">
                            <p className="text-gray-900 dark:text-white font-semibold mb-3">{t('who.needs.title')}</p>
                            <ul className="space-y-3 text-sm md:text-base">
                                <li className="flex gap-3">
                                    <span className="text-yellow-600 dark:text-yellow-400 font-bold">•</span>
                                    <span className="text-gray-700 dark:text-gray-300"><strong>{t('who.needs.candidates')}</strong> {t('who.needs.candidatesDesc')}</span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="text-yellow-600 dark:text-yellow-400 font-bold">•</span>
                                    <span className="text-gray-700 dark:text-gray-300"><strong>{t('who.needs.recruiters')}</strong> {t('who.needs.recruitersDesc')}</span>
                                </li>
                            </ul>
                        </motion.div>

                        <motion.p variants={itemVariants} className="text-gray-700 dark:text-gray-300 text-base md:text-lg leading-relaxed">
                            {t('who.bridge')} <strong className="text-yellow-600 dark:text-yellow-400">{t('who.platform')}</strong> {t('who.bridgeEnd')}
                        </motion.p>

                        <motion.p variants={itemVariants} className="text-gray-700 dark:text-gray-300 text-base md:text-lg leading-relaxed">
                            {t('who.values')}
                        </motion.p>

                        <motion.p variants={itemVariants} className="text-gray-700 dark:text-gray-300 text-base md:text-lg leading-relaxed italic">
                            {t('who.experience')} <strong className="text-yellow-600 dark:text-yellow-400 not-italic">{t('who.platform')}</strong> {t('who.experienceEnd')}
                        </motion.p>
                    </motion.div>
                </motion.section>

                {/* Mission Section */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="rounded-2xl bg-gradient-to-br from-gray-400 to-gray-500 dark:from-gray-600 dark:to-gray-700 shadow-lg p-6 md:p-8 lg:p-12"
                >
                    <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 md:mb-6">{t('sections.mission')}</h2>
                    <p className="text-gray-50 text-base md:text-lg lg:text-xl leading-relaxed max-w-3xl">
                        {t('mission.text')}
                    </p>
                </motion.section>

                {/* Values Section */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                >
                    <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-6 md:mb-10 flex items-center gap-3">
                        <div className="w-1 h-8 md:h-10 bg-gradient-to-b from-yellow-400 to-yellow-600 rounded-full" />
                        {t('sections.values')}
                    </h2>

                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        className="grid grid-cols-1 md:grid-cols-3 gap-6"
                    >
                        {values.map((value, i) => {
                            const Icon = value.icon;
                            return (
                                <motion.div
                                    key={value.id}
                                    variants={itemVariants}
                                    whileHover={{ scale: 1.05, y: -8 }}
                                    className="group relative"
                                >
                                    <div className="absolute inset-0 bg-gradient-to-r from-gray-400 to-gray-500 rounded-2xl blur-xl opacity-0 group-hover:opacity-40 transition-opacity" />
                                    <div className="relative bg-gradient-to-br from-gray-400 to-gray-500 dark:from-gray-600 dark:to-gray-700 rounded-2xl p-6 md:p-8 text-white shadow-lg h-full flex flex-col">
                                        <Icon className="w-10 h-10 md:w-12 md:h-12 text-yellow-300 mb-4 group-hover:scale-110 transition-transform" />
                                        <h3 className="text-lg md:text-xl font-bold mb-3">{value.title}</h3>
                                        <p className="text-gray-50 text-sm md:text-base flex-grow">{value.description}</p>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </motion.div>
                </motion.section>

                {/* Services Section */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                >
                    <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-6 md:mb-10 flex items-center gap-3">
                        <div className="w-1 h-8 md:h-10 bg-gradient-to-b from-yellow-400 to-yellow-600 rounded-full" />
                        {t('sections.services')}
                    </h2>

                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                    >
                        {services.map((service) => {
                            const Icon = service.icon;
                            return (
                                <motion.div
                                    key={service.id}
                                    variants={itemVariants}
                                    whileHover={{ scale: 1.05, y: -4 }}
                                    className="p-6 border-2 border-gray-200 dark:border-gray-700 rounded-2xl bg-gradient-to-br from-gray-50 to-white dark:from-gray-700 dark:to-gray-800 hover:border-yellow-500 transition-all group cursor-pointer"
                                >
                                    <div className="flex items-start gap-4 mb-4">
                                        <Icon className="w-7 h-7 md:w-8 md:h-8 text-yellow-500 flex-shrink-0 group-hover:scale-110 transition-transform" />
                                    </div>
                                    <h3 className="text-base md:text-lg font-bold text-gray-900 dark:text-white mb-3 group-hover:text-yellow-600 dark:group-hover:text-yellow-400 transition-colors">
                                        {service.title}
                                    </h3>
                                    <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 leading-relaxed">
                                        {service.description}
                                    </p>
                                </motion.div>
                            );
                        })}
                    </motion.div>
                </motion.section>

                {/* CTA Section */}
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                    className="rounded-2xl bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 dark:from-yellow-600 dark:via-yellow-700 dark:to-yellow-800 p-8 md:p-12 lg:p-16 shadow-2xl text-center"
                >
                    <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4">{t('cta.title')}</h2>
                    <p className="text-yellow-50 text-base md:text-lg mb-6 md:mb-8 max-w-2xl mx-auto">{t('cta.description')}</p>
                    <motion.button
                        onClick={() => navigate('/login')}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="px-6 md:px-8 py-3 bg-white text-yellow-600 font-bold rounded-lg hover:bg-gray-100 transition-all flex items-center justify-center gap-2 mx-auto text-sm md:text-base"
                    >
                        {t('cta.button')}
                        <ArrowRight className="w-5 h-5" />
                    </motion.button>
                </motion.section>
            </div>
        </div>
    );
};

export default AboutUs;